// ApiClient talks to the Kehai server's REST API. It keeps the session tokens
// in localStorage, refreshes an expired access token once per failed request,
// and unwraps the {data, meta, message} envelope every response uses.
import { acceptLanguage, t } from '../i18n/index.ts';

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
}

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

// tokenExpiry reads a JWT's expiry in milliseconds, or 0 when it has none.
function tokenExpiry(jwt: string): number {
  try {
    const payload = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const exp = JSON.parse(atob(payload)).exp;
    return typeof exp === 'number' ? exp * 1000 : 0;
  } catch {
    return 0;
  }
}

// ACTING_KEY holds a parent's acting token in a tab opened to manage a child
// (spec 001). It lives in that tab's sessionStorage alone, so
// it never outlives the tab or reaches the parent's own tabs.
const ACTING_KEY = 'kehai.acting_token';

// openChildTab opens a tab for a child's settings at once, while the click
// still allows it, and hands it the acting token once token resolves. It
// reports false when the browser blocked the tab.
export async function openChildTab(token: Promise<string>): Promise<boolean> {
  const tab = window.open('about:blank', '_blank');
  if (!tab) return false;
  try {
    tab.sessionStorage.setItem(ACTING_KEY, await token);
    tab.location.href = '/settings';
    tab.opener = null;
  } catch (e) {
    tab.close();
    throw e;
  }
  return true;
}

class ApiClient {
  private baseUrl: string;
  private token: string | null = null;
  private refreshing: Promise<boolean> | null = null;
  // acting is set in a tab managing a child: its token is the acting one,
  // held for the tab alone and never refreshed.
  readonly acting: boolean;

  constructor(baseUrl = '/api') {
    this.baseUrl = baseUrl;
    const actingToken = sessionStorage.getItem(ACTING_KEY);
    this.acting = actingToken !== null;
    this.token = actingToken ?? localStorage.getItem('access_token');
  }

  setToken(token: string | null) {
    this.token = token;
    if (this.acting) {
      if (!token) sessionStorage.removeItem(ACTING_KEY);
      return;
    }
    if (token) {
      localStorage.setItem('access_token', token);
    } else {
      localStorage.removeItem('access_token');
    }
  }

  getToken(): string | null {
    return this.token;
  }

  setRefreshToken(token: string | null) {
    if (this.acting) return;
    if (token) {
      localStorage.setItem('refresh_token', token);
    } else {
      localStorage.removeItem('refresh_token');
    }
  }

  getRefreshToken(): string | null {
    if (this.acting) return null;
    return localStorage.getItem('refresh_token');
  }

  // unwrap extracts usable data from the standard response envelope.
  // Paginated responses return {data, ...pagination}; everything else returns
  // the data field directly.
  private unwrap(json: any): any {
    if (!json || typeof json !== 'object') return json;
    if (json.meta?.pagination) {
      return { data: json.data, ...json.meta.pagination };
    }
    if ('data' in json) {
      return json.data;
    }
    return json;
  }

  private async send(path: string, opts: RequestOptions): Promise<Response> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept-Language': acceptLanguage(),
      ...opts.headers,
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    try {
      return await fetch(`${this.baseUrl}${path}`, {
        method: opts.method || 'GET',
        headers,
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      });
    } catch {
      throw { code: 'NETWORK', message: t('appErrorUnreachable') } as ApiError;
    }
  }

  private async request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const hadSession = !!this.token || !!this.getRefreshToken();
    let response = await this.send(path, opts);

    if (response.status === 401 && hadSession) {
      if (await this.tryRefresh()) {
        response = await this.send(path, opts);
      } else {
        this.endSession();
        throw { code: 'UNAUTHORIZED', message: t('appErrorSessionEnded') } as ApiError;
      }
    }

    if (!response.ok) {
      throw await this.parseError(response);
    }
    if (response.status === 204) {
      return undefined as T;
    }
    return this.unwrap(await response.json());
  }

  private async parseError(response: Response): Promise<ApiError> {
    try {
      const json = await response.json();
      return {
        code: json.error || 'UNKNOWN',
        message: json.message || response.statusText,
        details: json.details,
      };
    } catch {
      return { code: 'UNKNOWN', message: response.statusText };
    }
  }

  // download fetches a file the API streams, such as an export, and hands it
  // to the browser to save under its server-given name.
  async download(path: string, fallbackName: string): Promise<void> {
    const token = await this.freshToken();
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${path}`, { headers: { 'Accept-Language': acceptLanguage(), ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
    } catch {
      throw { code: 'NETWORK', message: t('appErrorUnreachable') } as ApiError;
    }
    if (!response.ok) throw await this.parseError(response);
    const name = /filename="([^"]+)"/.exec(response.headers.get('Content-Disposition') ?? '')?.[1] ?? fallbackName;
    const url = URL.createObjectURL(await response.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  // blob fetches a binary answer, such as a member's photo, or null on 404.
  async blob(path: string): Promise<Blob | null> {
    const token = await this.freshToken();
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${path}`, { headers: { 'Accept-Language': acceptLanguage(), ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
    } catch {
      throw { code: 'NETWORK', message: t('appErrorUnreachable') } as ApiError;
    }
    if (response.status === 404) return null;
    if (!response.ok) throw await this.parseError(response);
    return response.blob();
  }

  // putBlob sends a binary body, such as a photo, and resolves with the data.
  async putBlob<T>(path: string, body: Blob): Promise<T> {
    const token = await this.freshToken();
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${path}`, {
        method: 'PUT',
        headers: { 'Content-Type': body.type, 'Accept-Language': acceptLanguage(), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body,
      });
    } catch {
      throw { code: 'NETWORK', message: t('appErrorUnreachable') } as ApiError;
    }
    if (!response.ok) throw await this.parseError(response);
    return this.unwrap(await response.json());
  }

  // upload sends a multipart form with progress, which fetch cannot report,
  // and resolves with the response's data.
  async upload<T>(path: string, form: FormData, onProgress: (sent: number, total: number) => void): Promise<T> {
    const token = await this.freshToken();
    return new Promise<T>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${this.baseUrl}${path}`);
      xhr.setRequestHeader('Accept-Language', acceptLanguage());
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (e) => onProgress(e.loaded, e.total);
      xhr.onerror = () => reject({ code: 'NETWORK', message: t('appErrorUnreachable') } as ApiError);
      xhr.onload = () => {
        let json: any = null;
        try {
          json = JSON.parse(xhr.responseText);
        } catch {
          // An empty or broken body is reported below.
        }
        if (xhr.status >= 200 && xhr.status < 300) resolve(this.unwrap(json));
        else reject({ code: json?.error ?? 'HTTP_ERROR', message: json?.message ?? t('appErrorUploadFailed', { status: xhr.status }) } as ApiError);
      };
      xhr.send(form);
    });
  }

  // endSession forgets a sign-in the server no longer accepts and leaves for
  // the sign-in page. An acting session ends after its hour or with the
  // parent link; its tab falls back to the parent's own account.
  private endSession() {
    const acting = this.acting;
    this.setToken(null);
    this.setRefreshToken(null);
    window.location.href = acting ? '/settings' : '/login';
  }

  // freshToken returns an access token good for at least another 30 seconds,
  // refreshing it first when needed, or null when nobody is signed in. The
  // WebSocket carries the token in its URL and cannot see a 401, so it asks
  // here before every connect. A refresh the server refuses ends the session
  // as a refused request does.
  async freshToken(): Promise<string | null> {
    const token = this.getToken();
    if (!token) return null;
    if (tokenExpiry(token) - Date.now() > 30_000) return token;
    if (await this.tryRefresh()) return this.getToken();
    this.endSession();
    return null;
  }

  // tryRefresh shares one refresh among concurrent callers: the server
  // rotates the refresh token, so a second request with the old one fails.
  private tryRefresh(): Promise<boolean> {
    if (!this.refreshing) {
      this.refreshing = this.doRefresh().finally(() => (this.refreshing = null));
    }
    return this.refreshing;
  }

  private async doRefresh(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return false;
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept-Language': acceptLanguage() },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch {
      // A dropped connection says nothing about the session, which is still
      // valid on the server; clearing the tokens here would sign the user out
      // over a transient failure.
      throw { code: 'NETWORK', message: t('appErrorUnreachable') } as ApiError;
    }
    // Only a refusal ends the session. A 502 from the proxy or a 503 while
    // the server restarts says no more about it than a dropped connection.
    if (response.status === 400 || response.status === 401 || response.status === 403) return false;
    if (!response.ok) throw await this.parseError(response);
    const data = this.unwrap(await response.json());
    this.setToken(data.access_token);
    this.setRefreshToken(data.refresh_token);
    return true;
  }

  get<T>(path: string) {
    return this.request<T>(path);
  }

  post<T>(path: string, body?: unknown) {
    return this.request<T>(path, { method: 'POST', body });
  }

  put<T>(path: string, body?: unknown) {
    return this.request<T>(path, { method: 'PUT', body });
  }

  patch<T>(path: string, body?: unknown) {
    return this.request<T>(path, { method: 'PATCH', body });
  }

  // delete sends no body: the server does not read one on DELETE, so
  // parameters go in the query.
  delete<T>(path: string) {
    return this.request<T>(path, { method: 'DELETE' });
  }

  readonly auth = {
    login: <T>(username: string, password: string) =>
      this.post<T>('/auth/login', { username, password }),
    logout: () => this.post('/auth/logout'),
    me: <T>() => this.get<T>('/auth/me'),
  };
}

export const api = new ApiClient();
